import { debounce, isEmpty } from 'lodash';
import { useIntl } from 'react-intl';
import { ButtonUI, InputSearch, LoadingSkeleton, Selector, useQueryFetch } from 'sonata-react-components';

import { useDoohInventory } from '@/context/DoohInventoryContext';
import { getScreenInventoryFilters } from '@/services/dooh.service';
import { DoohInventoryFiltersLoadingStyled, DoohInventoryFiltersStyled } from './DoohInventoryFilters.styled';

import type { SelectorOptionPropsInterface } from 'sonata-react-components';
import type { ScreenInventoryFilters } from 'sonata-static-storage/lib/common/common.types';

function DoohInventoryFilters() {
  const { formatMessage } = useIntl();
  const { clearFilters, filters, inventoryByCountry, setFilters } = useDoohInventory();

  const { data, isLoading } = useQueryFetch(['doohInventoryFilters'], () => getScreenInventoryFilters());

  if (isLoading || !data) {
    return (
      <DoohInventoryFiltersLoadingStyled>
        <LoadingSkeleton height={40} />
      </DoohInventoryFiltersLoadingStyled>
    );
  }

  const getSelectorOptions = (field: keyof Omit<ScreenInventoryFilters, 'name'>) => {
    return (data?.[field] || [])
      .map(item => {
        const id = Object.values(item)[0];
        const name = Object.keys(item)[0];
        const label =
          field === 'format'
            ? formatMessage({
                defaultMessage: name,
                id: `doohInventory.filters.formatTypes.${name}`
              })
            : name;

        const idMap = {
          adExchange: id,
          country: id,
          format: id,
          publisher: id,
          venueType: id
        };

        return {
          id: idMap[field],
          label
        };
      })
      .filter(option => {
        if (option.label === 'undefined') {
          return false;
        }
        if (field === 'country') {
          return true;
        }
        if (field === 'adExchange' || field === 'publisher') {
          return (inventoryByCountry || []).find(item => item[`${field}Id`] === option.id);
        }
        const inventoryField = field === 'format' ? 'allowedAdTypes' : 'venueTypes';
        return (inventoryByCountry || []).find(item =>
          (item[inventoryField]?.details || []).some(d => String(d.id) === option.id)
        );
      });
  };

  const getSelectedOptions = (
    field: keyof Omit<ScreenInventoryFilters, 'name'>,
    options: SelectorOptionPropsInterface[]
  ) => {
    const selectedValues = filters?.[field] || [];
    return options.filter(o => selectedValues.includes(o.id as string));
  };

  const options = {
    adExchange: getSelectorOptions('adExchange'),
    country: getSelectorOptions('country'),
    format: getSelectorOptions('format'),
    publisher: getSelectorOptions('publisher'),
    venueType: getSelectorOptions('venueType')
  };

  const selectedOptions = {
    adExchange: getSelectedOptions('adExchange', options.adExchange),
    country: getSelectedOptions('country', options.country),
    format: getSelectedOptions('format', options.format),
    publisher: getSelectedOptions('publisher', options.publisher),
    venueType: getSelectedOptions('venueType', options.venueType)
  };

  const handleNameFilterChange = debounce((value: ScreenInventoryFilters['name']) => {
    setFilters('name', value);
  }, 500);

  const handleSelectorFilterChange = (field: Omit<keyof ScreenInventoryFilters, 'name'>, value: unknown) => {
    const filterValues = Array.isArray(value) ? value.map(fv => fv.id).filter(Boolean) : value;
    setFilters(field as keyof ScreenInventoryFilters, filterValues);
  };

  const isClearButtonDisabled = Object.keys(filters).length === 0 || Object.values(filters).every(isEmpty);

  return (
    <DoohInventoryFiltersStyled className="doohInventoryFilters">
      <div className="doohInventoryFilters__selectors">
        <Selector
          id="doohInventoryFilters-countriesFilter"
          key={`country-${options.country.map(e => e.id).join()}`} // Force re-render when options change
          onChange={value => handleSelectorFilterChange('country', value)}
          options={options.country}
          placeholder={formatMessage({
            defaultMessage: 'Select Country',
            id: 'doohInventory.filters.country'
          })}
          showDeselectOption
          showSelectedItems={false}
          type="multiple"
          value={selectedOptions.country}
        />
        <Selector
          id="doohInventoryFilters-formatsFilter"
          key={`format-${options.format.map(e => e.id).join()}`} // Force re-render when options change
          onChange={value => handleSelectorFilterChange('format', value)}
          options={options.format}
          placeholder={formatMessage({
            defaultMessage: 'Select Format',
            id: 'doohInventory.filters.format'
          })}
          showDeselectOption
          showSelectedItems={false}
          type="multiple"
          value={selectedOptions.format}
        />
        <Selector
          id="doohInventoryFilters-venueType"
          key={`venueType-${options.venueType.map(e => e.id).join()}`} // Force re-render when options change
          onChange={value => handleSelectorFilterChange('venueType', value)}
          options={options.venueType}
          placeholder={formatMessage({
            defaultMessage: 'Select Venue Type',
            id: 'doohInventory.filters.venueType'
          })}
          showDeselectOption
          showSelectedItems
          type="multiple"
          value={selectedOptions.venueType}
        />
        <Selector
          id="doohInventoryFilters-venueType"
          key={`publisher-${options.publisher.map(e => e.id).join()}`} // Force re-render when options change
          onChange={value => handleSelectorFilterChange('publisher', value)}
          options={options.publisher}
          placeholder={formatMessage({
            defaultMessage: 'Select Publisher',
            id: 'doohInventory.filters.publisher'
          })}
          showDeselectOption
          showSelectedItems
          type="multiple"
          value={selectedOptions.publisher}
        />
        <Selector
          id="doohInventoryFilters-venueType"
          key={`adExchange-${options.adExchange.map(e => e.id).join()}`} // Force re-render when options change
          onChange={value => handleSelectorFilterChange('adExchange', value)}
          options={options.adExchange}
          placeholder={formatMessage({
            defaultMessage: 'Select Adex',
            id: 'doohInventory.filters.adExchange'
          })}
          showDeselectOption
          showSelectedItems
          type="multiple"
          value={selectedOptions.adExchange}
        />
      </div>
      <div className="doohInventoryFilters__search">
        <div className="doohInventoryFilters__searchByName">
          <InputSearch
            defaultValue={filters?.name}
            id="doohInventoryFilters-searchByName"
            key={filters?.name}
            lens
            name="publisherName"
            onChange={handleNameFilterChange}
            placeholder={formatMessage({
              defaultMessage: 'Search by name...',
              id: 'doohInventory.filters.searchByName'
            })}
          />
        </div>
        <ButtonUI
          disabled={isClearButtonDisabled}
          icon="fa-light fa-trash"
          iconPosition="left"
          onClick={clearFilters}
          size="medium"
          type="secondary"
          visible
        />
      </div>
    </DoohInventoryFiltersStyled>
  );
}

export default DoohInventoryFilters;
